import React, { useEffect, useState, useRef, useCallback } from 'react';
import { CheckCircle, XCircle, DollarSign, Clock, AlertCircle, RefreshCw, Package } from 'lucide-react';
import { getPembelian, verifikasiPembelian, getVerifikasiCount } from '../../api';

const POLL_INTERVAL = 15000; // 15 seconds

export default function VerifikasiPembelian() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [error, setError] = useState('');
  const [nextPoll, setNextPoll] = useState(POLL_INTERVAL / 1000);
  const [liveStatus, setLiveStatus] = useState('active'); // 'active' | 'checking' | 'updated'
  const lastCountRef = useRef(-1);
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);

  const load = () => {
    setLoading(true);
    getPembelian()
      .then(res => {
        const filtered = (res.data || []).filter(d => d.status === 'Menunggu');
        setData(filtered);
        lastCountRef.current = filtered.length;
        setLiveStatus('active');
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  // Check count only (lightweight) — uses ref to avoid stale closure
  const checkCount = useCallback(async () => {
    try {
      setLiveStatus('checking');
      const res = await getVerifikasiCount();
      const newCount = res.data?.count || 0;
      if (newCount !== lastCountRef.current) {
        // Count changed — reload full data
        setLiveStatus('updated');
        const fullRes = await getPembelian();
        const filtered = (fullRes.data || []).filter(d => d.status === 'Menunggu');
        setData(filtered);
        lastCountRef.current = filtered.length;
        setTimeout(() => setLiveStatus('active'), 1000);
      } else {
        setLiveStatus('active');
      }
    } catch {
      setLiveStatus('active');
    }
  }, []);

  useEffect(() => {
    load();
    // Start polling interval
    intervalRef.current = setInterval(checkCount, POLL_INTERVAL);
    // Countdown ticker for UI
    countdownRef.current = setInterval(() => {
      setNextPoll(prev => prev > 0 ? prev - 1 : POLL_INTERVAL / 1000);
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  // Auto-refresh when tab becomes visible (complements polling)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset countdown after each poll completes
  useEffect(() => {
    if (liveStatus === 'active' || liveStatus === 'updated') {
      setNextPoll(POLL_INTERVAL / 1000);
    }
  }, [liveStatus]);

  const rupiah = (v) => `Rp ${(Number(v) || 0).toLocaleString('id-ID')}`;

  const handleVerifikasi = async (id, action) => {
    if (!window.confirm(
      `Yakin ${action === 'approve' ? 'menyetujui' : 'menolak'} pembelian ini?\n\n${
        action === 'approve'
          ? 'Stok sparepart akan ditambahkan dan jurnal akuntansi akan dibuat.'
          : 'Pembelian akan ditolak dan tidak diproses.'
      }`
    )) return;

    setProcessing(id);
    setError('');
    try {
      await verifikasiPembelian(id, action);
      setData(prev => {
        const newData = prev.filter(d => d.id !== id);
        lastCountRef.current = newData.length;
        return newData;
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal verifikasi');
    } finally {
      setProcessing(null);
    }
  };

  const totalMenunggu = data.length;
  const totalNilai = data.reduce((s, d) => s + (d.total_amount || 0), 0);
  const totalQty = data.reduce((s, d) => s + (d.total_qty || 0), 0);

  // Live indicator color
  const liveColor = liveStatus === 'checking' ? '#f59e0b'
    : liveStatus === 'updated' ? '#22c55e'
    : '#22c55e';
  const livePulse = liveStatus === 'checking';

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              Verifikasi Pembelian
              {totalMenunggu > 0 && (
                <span style={{
                  background: '#fef3c7', color: '#92400e',
                  padding: '2px 12px', borderRadius: 20,
                  fontSize: '0.85rem', fontWeight: 700
                }}>
                  {totalMenunggu} menunggu
                </span>
              )}
            </h2>
            <p className="page-desc" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Setujui atau tolak pembelian yang menunggu verifikasi
              {/* Live indicator */}
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontSize: '0.72rem', color: liveColor, fontWeight: 500,
                background: liveColor + '15', padding: '2px 8px',
                borderRadius: 10
              }}>
                <span style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: liveColor,
                  animation: livePulse ? 'pulse 1s infinite' : 'none',
                  display: 'inline-block'
                }} />
                {liveStatus === 'checking' ? 'Memeriksa...'
                  : liveStatus === 'updated' ? 'Diperbarui'
                  : `${nextPoll}s`}
              </span>
            </p>
          </div>
          <button className="btn btn-secondary" onClick={load} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spinner' : ''} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 20 }}>
        <div style={{ background: '#fefce8', borderRadius: 12, padding: '16px 20px', border: '1px solid #fde68a' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Clock size={20} style={{ color: '#d97706' }} />
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', color: '#92400e', fontWeight: 500 }}>Menunggu Verifikasi</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#92400e' }}>{totalMenunggu}</div>
            </div>
          </div>
        </div>
        <div style={{ background: '#f0fdf4', borderRadius: 12, padding: '16px 20px', border: '1px solid #bbf7d0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Package size={20} style={{ color: '#16a34a' }} />
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', color: '#166534', fontWeight: 500 }}>Total Qty</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#166534' }}>{totalQty}</div>
            </div>
          </div>
        </div>
        <div style={{ background: '#eff6ff', borderRadius: 12, padding: '16px 20px', border: '1px solid #bfdbfe' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <DollarSign size={20} style={{ color: '#2563eb' }} />
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', color: '#1e40af', fontWeight: 500 }}>Total Nilai</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#1e40af' }}>{rupiah(totalNilai)}</div>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="form-error" style={{ marginBottom: 16 }}>
          <AlertCircle size={14} /><span>{error}</span>
        </div>
      )}

      {loading && data.length === 0 ? (
        <div className="page-loading">Memuat data...</div>
      ) : totalMenunggu === 0 ? (
        <div style={{
          textAlign: 'center', padding: '60px 20px',
          background: '#f9fafb', borderRadius: 16,
          border: '1px dashed #d1d5db'
        }}>
          <CheckCircle size={48} style={{ color: '#22c55e', marginBottom: 12, opacity: 0.6 }} />
          <h3 style={{ color: '#374151', marginBottom: 6, fontSize: '1.1rem' }}>Tidak Ada Verifikasi Tertunda</h3>
          <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
            Semua pembelian sudah diverifikasi. Halaman ini otomatis memeriksa pembelian baru setiap {POLL_INTERVAL / 1000} detik.
          </p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>No. Ref</th>
                <th>Tanggal</th>
                <th>Supplier</th>
                <th>Qty</th>
                <th>Total</th>
                <th style={{ width: 100 }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {data.map((d, idx) => (
                <tr key={d.id} style={{
                  background: '#fffbeb',
                  animation: 'fadeIn 0.3s ease',
                  animationDelay: `${idx * 50}ms`,
                }}>
                  <td><strong style={{ fontSize: '0.8rem' }}>{d.no_ref}</strong></td>
                  <td>{d.tanggal}</td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{d.supplier_name || '-'}</div>
                    {d.supplier_phone && <div style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{d.supplier_phone}</div>}
                  </td>
                  <td>{d.total_qty}</td>
                  <td style={{ fontWeight: 700, color: '#059669' }}>{rupiah(d.total_amount)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        className="btn"
                        style={{
                          padding: '6px 14px', fontSize: '0.78rem',
                          background: '#059669', color: 'white',
                          border: 'none', borderRadius: 6, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: 5,
                          fontWeight: 600, opacity: processing === d.id ? 0.6 : 1
                        }}
                        onClick={() => handleVerifikasi(d.id, 'approve')}
                        disabled={processing === d.id}
                      >
                        <CheckCircle size={14} />
                        Setujui
                      </button>
                      <button
                        className="btn"
                        style={{
                          padding: '6px 14px', fontSize: '0.78rem',
                          background: '#dc2626', color: 'white',
                          border: 'none', borderRadius: 6, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: 5,
                          fontWeight: 600, opacity: processing === d.id ? 0.6 : 1
                        }}
                        onClick={() => handleVerifikasi(d.id, 'reject')}
                        disabled={processing === d.id}
                      >
                        <XCircle size={14} />
                        Tolak
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
